import { MsgBusActions, MsgBusEvents } from "../common/events";
import { Context, HandlersRegistry, OnStartCallbackPropsType } from "../common/types";
import { WidgetConfig, WidgetType, WIDGET_TYPES } from "../common/config";
import { Iframe, IframeCallback } from "./iframe";
import { MessageBusCallback } from "./messageBus";
import { logger } from "./logger";
import { MessageBus } from "./messageBus";
import { StateManager } from "./stateManager";
import { GtpService } from "./gtpService";
import { Analytics } from "./analytics";

type Dependencies = {
  msgBus: MessageBus;
  stateMngr: StateManager;
  gtpService: GtpService;
  analytics: Analytics;
  iframeHandlers: HandlersRegistry;
  msgBusHandlers: HandlersRegistry;
};

type WidgetInstance = {
  iframe: Iframe;
  widgetType: WidgetType;
};

export class App {
  private activeWidgets: Map<string, WidgetInstance> = new Map();

  constructor(private readonly deps: Dependencies) {}

  private getWidgetType = (widgetType: string): WidgetType | null => {
    const widgetTypeEntry = Object.entries(WIDGET_TYPES).find(([, value]) => value === widgetType);
    return widgetTypeEntry ? widgetTypeEntry[1] : null;
  };

  private registerIframeHandlers = (iframe: Iframe) => {
    const { iframeHandlers, msgBus, stateMngr, gtpService, analytics } = this.deps;
    const ctx: Context = { iframe, msgBus, stateMngr, gtpService, analytics };

    Object.entries(iframeHandlers).forEach((ele) => {
      const [topic, handler] = ele;
      const wrapped: IframeCallback = (payload: unknown) => handler(payload, ctx);
      iframe.subscribe(topic, wrapped);
    });
  };

  private registerMsgBusHandlers = () => {
    const { msgBusHandlers, msgBus, stateMngr, gtpService, analytics } = this.deps;

    Object.entries(msgBusHandlers).forEach((ele) => {
      const [topic, handler] = ele;
      const wrapped: MessageBusCallback = (_, payload: unknown) => {
        this.activeWidgets.forEach(({ iframe }) => {
          const ctx: Context = { iframe, msgBus, stateMngr, gtpService, analytics };
          handler(payload, ctx);
        });
      };
      msgBus.subscribe(topic, wrapped);
    });
  };

  private onWidgetStart = () => {
    this.deps.msgBus.subscribe(
      MsgBusEvents.START,
      (_, { selector, widgetType, headerDisabled }: OnStartCallbackPropsType) => {
        logger.log("onWidgetStart ", selector, widgetType, headerDisabled);

        const validWidgetType = this.getWidgetType(widgetType);
        if (!validWidgetType) {
          throw new Error(`Unsupported widget type: ${widgetType}`);
        }

        if (this.activeWidgets.has(selector)) {
          logger.log(`Widget already started for selector: ${selector}`);
          return;
        }

        const iframe = new Iframe();
        iframe.load(selector, headerDisabled, validWidgetType);
        this.registerIframeHandlers(iframe);

        if (this.activeWidgets.size === 0) {
          this.registerMsgBusHandlers();
        }

        this.deps.msgBus.publish(MsgBusActions.START_SUCCESS, {
          id: selector,
          widgetType,
        });
        this.activeWidgets.set(selector, { iframe, widgetType: validWidgetType });
      },
    );
  };

  private onWidgetStop = () => {
    this.deps.msgBus.subscribe(MsgBusEvents.STOP, (_, { id }: { id: string }) => {
      const widget = this.activeWidgets.get(id);
      if (!widget) return;

      widget.iframe.dispose();
      this.activeWidgets.delete(id);
      this.deps.msgBus.publish(MsgBusActions.STOP_SUCCESS, { id });

      if (this.activeWidgets.size === 0) {
        this.deps.msgBus.dispose();
      }
    });
  };

  private onWidgetInitSuccess = () => {
    this.deps.msgBus.publish(MsgBusActions.INIT_SUCCESS, {
      id: WidgetConfig.ID_PREFIX,
    });
  };

  private onWidgetInitError = (err: Error) => {
    this.activeWidgets.forEach((widget) => {
      widget.iframe.dispose();
    });
    this.activeWidgets.clear();
    this.deps.msgBus.dispose();
    this.deps.msgBus.publish(MsgBusActions.INIT_ERROR, {
      id: WidgetConfig.ID_PREFIX,
      reason: err.message,
    });
  };

  public init = async () => {
    try {
      this.onWidgetStart();
      this.onWidgetStop();
      this.onWidgetInitSuccess();
    } catch (error) {
      console.error("Error during widget init:", error);
      this.onWidgetInitError(error);
    }
  };
}
